// SCR-485, Keys (REQ-174, REQ-176): every key of the installation with its person, name, client,
// source, created, last used and expiry, filtered by person and standing, exported as CSV; a key's
// row opens its summary, where a live one is revoked. The same screen serves the customer admin on the proxy (proxySource,
// revoke at once) and AllCode staff in the admin app (staffSource, the mirror stamped "as
// reported by the proxy at", and a revoke that queues a command with a reason). A key is shown
// by its name and last four characters only (S2). Also the parts both SCR-485 tabs share.
import {
  ACTION_LABELS,
  actionWarning,
  COMMAND_STATUS_LABELS,
  failureMessage,
  filterKeys,
  formatDay,
  isKeyLive,
  isStale,
  keysCsv,
  keyStanding,
  keyTitle,
  latestCommand,
  MAX_REASON,
  REASON_CODE_LABELS,
  REASON_CODES,
  reasonProblem,
  reportedLine,
  type Action,
  type ActionKind,
  type Audience,
  type Command,
  type Failure,
  type IdentitySource,
  type KeyFilter,
  type KeyItem,
  type ReasonCode,
  type IdentityScope,
  type Snapshot,
  type StaffReason,
} from "./source";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { FOCUSABLE, ROW_OPEN_CLASS, rowClickHandler } from "../../primitives/row-click";

/** The identity tabs' links; Invites shows only where the shell mounts it. */
export interface IdentityTabLinks {
  keys: string;
  people: string;
  invites?: string;
}

export type IdentityTab = IdentityScope | "invites";

export interface ScreenProps {
  source: IdentitySource;
  /** Now, for live/expired and the stale-report banner. */
  now?: () => Date;
  /** The Keys, People (and Invites) tab links; the tabs are left out without them. */
  tabs?: IdentityTabLinks;
  /** A client-side navigation for the tab links; a plain link without it. */
  navigate?: (href: string) => void;
}

// ------------------------------------------------------------------------- shared by both tabs

export type LoadState = { phase: "loading" } | { phase: "failed"; failure: Failure } | { phase: "ready"; snap: Snapshot };

/** The tab's list, loaded on mount and again on reload(). */
export function useSnapshot(source: IdentitySource, scope: IdentityScope): { state: LoadState; reload(): void } {
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ phase: "loading" });
    void source.load(scope).then((result) => {
      if (!live) return;
      setState(result.ok ? { phase: "ready", snap: result.value } : { phase: "failed", failure: result.failure });
    });
    return () => {
      live = false;
    };
  }, [source, scope, round]);
  return { state, reload: () => setRound((n) => n + 1) };
}

export function IdentityTabs({ current, tabs, navigate }: { current: IdentityTab; tabs: IdentityTabLinks; navigate?: ((href: string) => void) | undefined }) {
  const link = (scope: IdentityTab, href: string, label: string) => (
    <a
      href={href}
      aria-current={scope === current ? "page" : undefined}
      onClick={(event: MouseEvent) => {
        if (!navigate || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(href);
      }}
    >
      {label}
    </a>
  );
  return (
    <nav class="tabs idn-tabs" aria-label="Keys and people">
      {link("keys", tabs.keys, "Keys")}
      {link("people", tabs.people, "People")}
      {tabs.invites === undefined ? null : link("invites", tabs.invites, "Invites")}
    </nav>
  );
}

/** The heading of a tab, with the staff twin's stamp. */
export function ScreenHead({ title, titleId, lede, audience }: { title: string; titleId?: string; lede: string; audience: Audience }) {
  return (
    <header class="idn-head">
      <h1 id={titleId}>{title}</h1>
      <p class="idn-lede">{lede}</p>
      {audience === "staff" ? <p class="idn-lede">AllCode support can only take access away. Every action carries a reason the customer reads.</p> : null}
    </header>
  );
}

export function Skeleton() {
  return (
    <div class="idn-skeleton" role="status" aria-busy="true">
      <span class="visually-hidden">Loading…</span>
      {[0, 1, 2].map((n) => (
        <div key={n} class="idn-bone" aria-hidden="true" />
      ))}
    </div>
  );
}

/** The states before the list: loading, failed with Retry, forbidden, session ended, and the modes. */
export function Unready({ state, audience, what, onRetry }: { state: LoadState; audience: Audience; what: string; onRetry(): void }) {
  if (state.phase === "loading") return <Skeleton />;
  if (state.phase === "failed") {
    if (state.failure === "forbidden" || state.failure === "session-ended") {
      return (
        <div class="card idn-note" role="alert">
          <h2>{state.failure === "forbidden" ? "Not available" : "Your session ended"}</h2>
          <p>{failureMessage(state.failure, audience)}</p>
        </div>
      );
    }
    return (
      <div class="banner banner-error" role="alert">
        <p>
          Couldn't load the {what}. {failureMessage(state.failure, audience)}
        </p>
        <button type="button" class="button" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  const { mode } = state.snap;
  if (mode === "waiting") {
    return (
      <div class="card idn-note">
        <h2>Waiting for this proxy's settings</h2>
        <p>This proxy has not received its settings from AllCode yet. The {what} appear here once it has.</p>
      </div>
    );
  }
  if (mode === "central") {
    return (
      <div class="card idn-note">
        <h2>Not on this proxy yet</h2>
        <p>Sign-in for this installation is still served centrally. The {what} are managed here once sign-in moves to this proxy.</p>
      </div>
    );
  }
  return (
    <div class="card idn-note">
      <h2>No report yet</h2>
      <p>This proxy has not reported its people and keys yet. It reports every 5 minutes once it runs this release.</p>
    </div>
  );
}

/** "As reported by the proxy at", and a banner when that is more than 30 minutes ago. */
export function StaffStamp({ snap, now }: { snap: Snapshot; now: Date }) {
  if (snap.reportedAt === null) return null;
  return (
    <>
      <p class="idn-stamp">{reportedLine(snap.reportedAt)}</p>
      {isStale(snap.reportedAt, now) ? (
        <div class="banner banner-warning" role="status">
          <p>This proxy hasn't reported in over 30 minutes. What you see may be out of date, and queued commands wait until it is back.</p>
        </div>
      ) : null}
    </>
  );
}

export function CommandCell({ command }: { command: Command | null }) {
  if (command === null) return <span class="idn-quiet">None</span>;
  const tone = command.status === "applied" ? "chip-green" : command.status === "queued" ? "chip-blue" : "chip-amber";
  return (
    <span class="idn-stack">
      <span class={`chip ${tone}`}>{COMMAND_STATUS_LABELS[command.status]}</span>
      <span class="idn-sub">{formatDay(command.issuedAt)}</span>
    </span>
  );
}

const REASON_PROBLEMS = {
  "too-short": "Write at least 10 characters. The customer reads this reason.",
  "too-long": `Keep it to ${MAX_REASON} characters.`,
};

/**
 * The confirmation of one action, inline under its row. Staff must choose a reason code and write
 * a reason (decisions 1 and 13); a customer confirms with one click.
 */
export function ConfirmAction({
  audience,
  kind,
  subject,
  busy,
  failure,
  onConfirm,
  onCancel,
}: {
  audience: Audience;
  kind: ActionKind;
  subject: string;
  busy: boolean;
  failure: Failure | null;
  onConfirm(reason: StaffReason | null): void;
  onCancel(): void;
}) {
  const [code, setCode] = useState<ReasonCode>("customer_request");
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const problem = audience === "staff" ? reasonProblem(text) : null;
  const id = `idn-${kind}-${subject.replace(/[^A-Za-z0-9]/g, "")}`;
  const submit = (event: Event) => {
    event.preventDefault();
    setTouched(true);
    if (problem === null && !busy) onConfirm(audience === "staff" ? { code, text } : null);
  };
  const verb = audience === "staff" ? `Queue ${ACTION_LABELS[kind].toLowerCase()}` : ACTION_LABELS[kind];
  return (
    <form class="idn-confirm" onSubmit={submit} aria-labelledby={`${id}-q`}>
      <p id={`${id}-q`} class="idn-confirm-text">
        {ACTION_LABELS[kind]} {subject}? {actionWarning(audience, kind)}
      </p>
      {audience === "staff" ? (
        <div class="idn-reason">
          <div class="field">
            {/* help-exempt: shared with web/home, which has no help topics; the label says it all. */}
            <label for={`${id}-code`}>Reason code</label>
            <select id={`${id}-code`} value={code} onChange={(event) => setCode((event.currentTarget as HTMLSelectElement).value as ReasonCode)}>
              {REASON_CODES.map((c) => (
                <option key={c} value={c}>
                  {REASON_CODE_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            {/* help-exempt: shared with web/home, which has no help topics; the label says it all. */}
            <label for={`${id}-reason`}>Reason, shown to the customer</label>
            <textarea
              id={`${id}-reason`}
              rows={2}
              maxLength={MAX_REASON}
              value={text}
              aria-invalid={touched && problem !== null}
              aria-describedby={`${id}-reason-hint`}
              onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
            />
            <p id={`${id}-reason-hint`} class={touched && problem ? "field-error" : "field-hint"}>
              {touched && problem ? REASON_PROBLEMS[problem] : `${[...text.trim()].length}/${MAX_REASON}, at least 10`}
            </p>
          </div>
        </div>
      ) : null}
      {failure ? (
        <p class="field-error" role="alert">
          {failureMessage(failure, audience)}
        </p>
      ) : null}
      <div class="idn-actions">
        <button type="submit" class={kind === "approve" || kind === "enable" ? "button button-primary" : "button button-danger"} disabled={busy}>
          {busy ? "Working…" : verb}
        </button>
        <button type="button" class="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Which row's action is being confirmed or sent, and how the last try ended. */
export type Pending = { target: string; kind: ActionKind; busy: boolean; failure: Failure | null } | null;

/** The pending action and what runs it: on success the list reloads and a notice says what happened. */
export function useActions(source: IdentitySource, reload: () => void) {
  const [pending, setPending] = useState<Pending>(null);
  const [notice, setNotice] = useState("");
  /** Send the action; `after` runs once it succeeded (a summary closes itself with it). */
  const run = (target: string, kind: ActionKind, action: Action, reason: StaffReason | null, done: string, after?: () => void) => {
    setPending({ target, kind, busy: true, failure: null });
    void source.act(action, reason).then((result) => {
      if (result.ok) {
        setPending(null);
        after?.();
        setNotice(source.audience === "staff" ? `Queued: ${done}. The proxy applies it when it next pulls commands.` : `Done: ${done}.`);
        reload();
      } else {
        setPending({ target, kind, busy: false, failure: result.failure });
      }
    });
  };
  const ask = (target: string, kind: ActionKind) => setPending({ target, kind, busy: false, failure: null });
  return { pending, notice, run, ask, cancel: () => setPending(null) };
}

export interface RowSummaryProps {
  title: string;
  facts: readonly { label: string; value: ComponentChildren }[];
  onClose(): void;
  /** The row's actions, or the confirmation of one. */
  children?: ComponentChildren;
}

/**
 * A row's summary, the row-click tables' detail: a native modal <dialog> (the page goes inert),
 * Tab kept inside it, Esc closes, and focus goes back to what opened it. web/admin's ui/dialog.tsx
 * semantics, built on the bound preact (this module imports none). Open while rendered.
 */
export function RowSummary({ title, facts, onClose, children }: RowSummaryProps) {
  const [node, setNode] = useState<HTMLDialogElement | null>(null);
  const titleId = `idn-summary-${title.replace(/[^A-Za-z0-9]/g, "")}`;
  useEffect(() => {
    if (!node) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node.showModal();
    return () => {
      node.close();
      if (opener?.isConnected) opener.focus();
    };
  }, [node]);
  const trapTab = (event: KeyboardEvent) => {
    if (event.key !== "Tab" || !node) return;
    const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <dialog
      ref={setNode}
      class="dialog idn-summary"
      aria-labelledby={titleId}
      onKeyDown={trapTab}
      onCancel={(event: Event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div class="dialog-head">
        <h2 id={titleId} class="dialog-title">
          {title}
        </h2>
      </div>
      <dl class="idn-summary-facts">
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
      {children}
      <div class="idn-actions idn-summary-close">
        <button type="button" class="button" onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  );
}

/** A clickable row's open button, first in its first cell (ui/row-click.ts). */
export function RowOpen({ label }: { label: string }) {
  return <button type="button" class={ROW_OPEN_CLASS} aria-label={label} />;
}

/** Download a CSV built in the page: no request, so nothing a filter hides leaves the browser. */
export function downloadCsv(name: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ------------------------------------------------------------------------------------ the keys

export function KeysScreen({ source, now = () => new Date(), tabs, navigate }: ScreenProps) {
  const { state, reload } = useSnapshot(source, "keys");
  const actions = useActions(source, reload);
  const [filter, setFilter] = useState<KeyFilter>({ person: "", show: "live" });
  const audience = source.audience;
  const at = now();

  return (
    <section class="idn" aria-labelledby="idn-keys-title">
      {tabs ? <IdentityTabs current="keys" tabs={tabs} navigate={navigate} /> : null}
      <ScreenHead
        title="Keys"
        audience={audience}
        lede="One key for each machine a person set up. Revoking one stops it within a minute; the person sets the machine up again to get a new one."
      />
      <p class="idn-status" role="status" aria-live="polite">
        {state.phase === "loading" ? "Loading keys…" : actions.notice}
      </p>
      {state.phase === "ready" && state.snap.mode === "ready" ? (
        <KeysTable snap={state.snap} at={at} audience={audience} filter={filter} setFilter={setFilter} actions={actions} />
      ) : (
        <Unready state={state} audience={audience} what="keys" onRetry={reload} />
      )}
    </section>
  );
}

function KeysTable({
  snap,
  at,
  audience,
  filter,
  setFilter,
  actions,
}: {
  snap: Snapshot;
  at: Date;
  audience: Audience;
  filter: KeyFilter;
  setFilter(next: KeyFilter): void;
  actions: ReturnType<typeof useActions>;
}) {
  const rows = filterKeys(snap.keys, filter, at);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = snap.keys.find((key) => key.keyId === selectedId) ?? null;
  const close = () => {
    actions.cancel();
    setSelectedId(null);
  };
  return (
    <>
      {audience === "staff" ? <StaffStamp snap={snap} now={at} /> : null}
      <div class="filters idn-filters">
        <div class="field">
          {/* help-exempt: shared with web/home, which has no help topics; the label says it all. */}
          <label for="idn-keys-person">Person</label>
          <input
            id="idn-keys-person"
            type="search"
            value={filter.person}
            placeholder="Name or email"
            onInput={(event) => setFilter({ ...filter, person: (event.currentTarget as HTMLInputElement).value })}
          />
        </div>
        <div class="field">
          {/* help-exempt: shared with web/home, which has no help topics; the label says it all. */}
          <label for="idn-keys-show">Show</label>
          <select
            id="idn-keys-show"
            value={filter.show}
            onChange={(event) => setFilter({ ...filter, show: (event.currentTarget as HTMLSelectElement).value as KeyFilter["show"] })}
          >
            <option value="live">In use</option>
            <option value="revoked">Revoked or expired</option>
            <option value="all">All</option>
          </select>
        </div>
        <button type="button" class="button idn-export" disabled={rows.length === 0} onClick={() => downloadCsv("nexus-keys.csv", keysCsv(rows, at))}>
          Export CSV
        </button>
      </div>
      {snap.keys.length === 0 ? (
        <div class="card idn-note idn-empty">
          <h2>No keys yet</h2>
          <p>Keys appear here when people sign in on this proxy and set up a machine.</p>
        </div>
      ) : rows.length === 0 ? (
        <p class="idn-quiet">No key matches these filters.</p>
      ) : (
        <div class="table-scroll">
          <table class="data-table idn-table">
            <caption class="visually-hidden">Keys</caption>
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Key</th>
                <th scope="col">Client</th>
                <th scope="col">Source</th>
                <th scope="col">Created</th>
                <th scope="col">Last used</th>
                <th scope="col">Expires</th>
                <th scope="col">Status</th>
                {audience === "staff" ? <th scope="col">Command</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((key) => (
                <KeyRow key={key.keyId} keyItem={key} at={at} audience={audience} commands={snap.commands} onOpen={() => setSelectedId(key.keyId)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected ? <KeySummary keyItem={selected} at={at} audience={audience} commands={snap.commands} actions={actions} onClose={close} /> : null}
    </>
  );
}

function KeyTitle({ keyItem }: { keyItem: KeyItem }) {
  const title = keyTitle(keyItem);
  return (
    <span class="idn-key-title">
      {title}{" "}
      <span class="idn-hint">
        <span class="visually-hidden">ending in </span>
        <span aria-hidden="true">····</span>
        {keyItem.hint}
      </span>
    </span>
  );
}

const sourceText = (keyItem: KeyItem) => (keyItem.legacy ? `${keyItem.source} (imported)` : keyItem.source);
const expiresText = (keyItem: KeyItem) => (keyItem.expiresAt === null ? "never" : formatDay(keyItem.expiresAt));

function KeyRow({ keyItem, at, audience, commands, onOpen }: { keyItem: KeyItem; at: Date; audience: Audience; commands: readonly Command[]; onOpen(): void }) {
  const title = keyTitle(keyItem);
  const live = isKeyLive(keyItem, at);
  return (
    <tr class={live ? "idn-row row-clickable" : "idn-row idn-row-ended row-clickable"} onClick={rowClickHandler(onOpen)}>
      <td>
        <RowOpen label={`Key ${title}, ${keyItem.owner}`} />
        <span class="idn-stack">
          <span>{keyItem.owner}</span>
          {keyItem.ownerEmail && keyItem.ownerEmail !== keyItem.owner ? <span class="idn-sub">{keyItem.ownerEmail}</span> : null}
        </span>
      </td>
      <td>
        <span class="idn-stack">
          <KeyTitle keyItem={keyItem} />
          {title !== keyItem.name ? <span class="idn-sub">{keyItem.name}</span> : null}
          {keyItem.deviceName && !keyItem.name.startsWith(keyItem.deviceName) ? <span class="idn-sub">Device: {keyItem.deviceName}</span> : null}
        </span>
      </td>
      <td>{keyItem.client || "Unknown"}</td>
      <td>{sourceText(keyItem)}</td>
      <td>{formatDay(keyItem.createdAt)}</td>
      <td>{formatDay(keyItem.lastUsedDay)}</td>
      <td>{expiresText(keyItem)}</td>
      <td>{keyStanding(keyItem, at)}</td>
      {audience === "staff" ? (
        <td>
          <CommandCell command={latestCommand(commands, keyItem.keyId)} />
        </td>
      ) : null}
    </tr>
  );
}

/** A key's summary: its facts, and Revoke (with its confirmation) while it is live and none is queued. */
function KeySummary({
  keyItem,
  at,
  audience,
  commands,
  actions,
  onClose,
}: {
  keyItem: KeyItem;
  at: Date;
  audience: Audience;
  commands: readonly Command[];
  actions: ReturnType<typeof useActions>;
  onClose(): void;
}) {
  const title = keyTitle(keyItem);
  const pending = actions.pending?.target === keyItem.keyId ? actions.pending : null;
  const command = latestCommand(commands, keyItem.keyId);
  const queued = command?.status === "queued" && command.kind === "revoke_key";
  const facts = [
    { label: "Person", value: keyItem.ownerEmail && keyItem.ownerEmail !== keyItem.owner ? `${keyItem.owner} (${keyItem.ownerEmail})` : keyItem.owner },
    { label: "Key", value: <KeyTitle keyItem={keyItem} /> },
    { label: "Client", value: keyItem.client || "Unknown" },
    { label: "Source", value: sourceText(keyItem) },
    { label: "Created", value: formatDay(keyItem.createdAt) },
    { label: "Last used", value: formatDay(keyItem.lastUsedDay) },
    { label: "Expires", value: expiresText(keyItem) },
    { label: "Status", value: keyStanding(keyItem, at) },
    ...(audience === "staff" ? [{ label: "Command", value: <CommandCell command={command} /> }] : []),
  ];
  return (
    <RowSummary title={title} facts={facts} onClose={onClose}>
      {pending ? (
        <ConfirmAction
          audience={audience}
          kind="revoke"
          subject={`${title} (····${keyItem.hint})`}
          busy={pending.busy}
          failure={pending.failure}
          onCancel={actions.cancel}
          onConfirm={(reason) => actions.run(keyItem.keyId, "revoke", { kind: "revoke", keyId: keyItem.keyId }, reason, `revoke ${title}`, onClose)}
        />
      ) : isKeyLive(keyItem, at) && !queued ? (
        <div class="idn-actions">
          <button type="button" class="button button-danger" onClick={() => actions.ask(keyItem.keyId, "revoke")}>
            Revoke
          </button>
        </div>
      ) : null}
    </RowSummary>
  );
}
